import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Form } from '@zolteam/zol-react-form';
import { useMutation } from '@tanstack/react-query';
import {
  Button, EditPanel, Formblock, FormSection, generateTableFromSchema, Message, TableSkeleton, Textfield,
  useTable,
} from '@zolteam/zol-admin-ui';
import { useEffect, useState } from 'react';
import { useGetAirport } from '../../services/airports/airportsQueries';
import { useAirports } from '../../domain/airports/useAirports';
import { createAirport, updateAirport } from '../../services/airports/airportService';
import fr from '../../locales/fr.json';
import TerminalDetails from '../../components/organisms/terminalDetails/TerminalDetails';

const AIRPORT_FORM = 'airportForm';

/**
 * @returns {JSX.Element}
 */
export default function AirportNew() {
  const { id } = useParams();

  // local state
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [coordinate, setCoordinate] = useState('');
  const [isoCountry, setIsoCountry] = useState('');
  const [iataCode, setIataCode] = useState('');
  const [ident, setIdent] = useState('');

  const [searchParam, setSearchParams] = useSearchParams();
  const sort = searchParam.get('sort') || 'codeSort';
  const order = searchParam.get('order') || 'desc';
  const [currentTerminal, setCurrentTerminal] = useState(null);

  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    handleColumnSort,
  } = useTable({
    setParams:
    /**
     * Set the search params.
     * @param {object} params
     * @returns {void}
     */
    (params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
    // @ts-ignore
    defaultSelectedRows: [],
  });

  // json schema
  const UserListSchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Customer',
    properties: {},
    additionalProperties: false,
  };

  const {
    data: airport,
    isLoading: isLoadingAirport,
    refetch,
  } = useGetAirport(
    id,
    { options: { enabled: !!id } },
  );
  const {
    AIRPORT_FORM_SCHEMA,
    airportDefaultValues,
    airportFormDataToPayload,
  } = useAirports({
    airport,
  });

  // API Calls
  const postCreateAirportMutation = useMutation(createAirport, {
    onSuccess: async (data) => {
      // `createAirport` returns the created resource containing its `id`.
      // Redirect to the newly created airport detail page.
      const newId = data?.id ?? null;
      if (newId != null) {
        navigate(`/airports/${newId}`);
      } else {
        navigate(localStorage.getItem('airportListUrl') || '/airports');
      }
    },
  });

  const putUpdateAirportMutation = useMutation(updateAirport, {
    onSuccess: async () => {
      navigate(localStorage.getItem('airportListUrl') || '/airports');
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('putUpdateAirportMutation error', error);
    },
  });

  // Handlers
  /**
   * Handles the form submission to create a new user.
   * @param {any} formValues - The values from the form.
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    try {
      if (!formValues) {
        return;
      }

      const body = airportFormDataToPayload({ formValues });
      if (id) {
        putUpdateAirportMutation.mutate({ ...body, id: parseInt(id, 10) });
      } else {
        postCreateAirportMutation.mutate(body);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DEBUG] onSubmit error', e);
    }
  };

  /**
   * Set the status.
   * @param {string} newName - The status.
   * @param {any} formInfo
   */
  const handleSetName = (newName, formInfo) => {
    setName(newName);
    formInfo.formMethods.setValue('name', newName, { shouldValidate: true });
  };

  /**
   * Set the code.
   * @param {string} newCode - The code.
   * @param {any} formInfo
   */
  const handleSetCode = (newCode, formInfo) => {
    setCode(newCode);
    formInfo.formMethods.setValue('code', newCode, { shouldValidate: true });
  };

  /**
   * Set the municipality.
   * @param {string} newMunicipality - The municipality.
   * @param {any} formInfo
   */
  const handleSetMunicipality = (newMunicipality, formInfo) => {
    setMunicipality(newMunicipality);
    formInfo.formMethods.setValue('municipality', newMunicipality, { shouldValidate: true });
    // formInfo.formMethods.setValue('city', newMunicipality, { shouldValidate: true });
  };

  /**
   * Set the coordinate as a comma separated string "lat,lon".
   * Also update the form value as an array [lat, lon].
   * @param {string} newCoordinate - The coordinate string
   * @param {any} formInfo
   */
  const handleSetCoordinate = (newCoordinate, formInfo) => {
    setCoordinate(newCoordinate);
    // split by comma into lat and lon (trim spaces)
    const parts = (newCoordinate || '').split(',').map((p) => p.trim());
    const lat = parts[0] || '';
    const lon = parts[1] || '';
    // set both a `coordinate` (string) and the form `coordinate` as array
    try {
      formInfo.formMethods.setValue('coordinate', [lat, lon], { shouldValidate: true });
    } catch (e) {
      // fallback: set string value if form expects string
      try {
        formInfo.formMethods.setValue('coordinate', newCoordinate, { shouldValidate: true });
      } catch (err) {
        // ignore
      }
    }
  };

  /**
   * Set the isoCountry.
   * @param {string} newIsoCountry - The isoCountry.
   * @param {any} formInfo
   */
  const handleSetIsoCountry = (newIsoCountry, formInfo) => {
    setIsoCountry(newIsoCountry);
    formInfo.formMethods.setValue('isoCountry', newIsoCountry, { shouldValidate: true });
  };

  /**
   * Set the iataCode.
   * @param {string} newIataCode - The iataCode.
   * @param {any} formInfo
   */
  const handleSetIataCode = (newIataCode, formInfo) => {
    setIataCode(newIataCode);
    formInfo.formMethods.setValue('iataCode', newIataCode, { shouldValidate: true });
  };

  /**
   * Set the ident.
   * @param {string} newIdent - The ident.
   * @param {any} formInfo
   */
  const handleSetIdent = (newIdent, formInfo) => {
    setIdent(newIdent);
    formInfo.formMethods.setValue('ident', newIdent, { shouldValidate: true });
  };

  // Handlers
  /**
   * Handle the click on the new content button.
   * @returns {void}
   */
  const handleNewContentClick = () => navigate('terminals/new');

  /**
   * Handle the click on a table row.
   * @param {any} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => {
    setCurrentTerminal(row);
  };

  /**
   * Handle the close of the panel.
   * @returns {void}
   */
  const handleCloseDetailPanel = () => {
    setCurrentTerminal(null);
    refetch();
  };

  useEffect(() => {
    if (airport) {
      setName(airport.name || '');
      setCode(airport.code || '');
      // Prefer `airport.municipality` (schema uses `municipality`), fall back to `municipality` if present
      setMunicipality(airport.municipality || '');
      // Build coordinate string from airport.coordinate (array) or latitudeDeg/longitudeDeg
      if (Array.isArray(airport.coordinate) && airport.coordinate.length) {
        const lat = airport.coordinate[0] ?? '';
        const lon = airport.coordinate[1] ?? '';
        setCoordinate([lat, lon].filter(Boolean).join(',') || '');
      } else if (airport.latitudeDeg || airport.longitudeDeg) {
        setCoordinate(`${airport.latitudeDeg || ''},${airport.longitudeDeg || ''}`);
      } else {
        setCoordinate('');
      }
      setIsoCountry(airport.isoCountry || '');
      setIataCode(airport.iataCode || '');
      setIdent(airport.ident || '');
    }
  }, [airport]);

  return (
    <div className="padding-l relative">
      <div
        id="form-header"
        className="d-flex f-row gap-s f-wrap justify-between padding-top-l sticky-top background-color-neutral-00 foreground-index"
      >
        <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
          {airport ? t('airportForm.title.update') : t('airportForm.title.new')}
        </h1>
      </div>

      {/* terminal detail view */}
      <EditPanel
        id="terminalDetail"
        onClose={handleCloseDetailPanel}
        isOpen={!!currentTerminal}
      >
        {currentTerminal
          ? (
            <TerminalDetails
              airportId={id}
              terminalId={currentTerminal.id}
              handleClosePanel={handleCloseDetailPanel}
            />
          ) : null}
      </EditPanel>

      <div className="form-content">
        {postCreateAirportMutation.isError ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${postCreateAirportMutation.error
                // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}
        {putUpdateAirportMutation.isError ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${putUpdateAirportMutation.error
                // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}

        {(id && airport) || !id ? (
          <Form
            name={AIRPORT_FORM}
            callbacks={{ onSubmit }}
            schema={AIRPORT_FORM_SCHEMA}
            defaultValues={airportDefaultValues}
            translations={{ formValidation: fr.formValidation }}
          >
            {(_formConfig, formInfo) => (
              <>
                <div>
                  <FormSection label={t('airportForm.section.general')} className="margin-top-m">
                    <div className="d-flex f-row gap-m f-wrap align-end">
                      <Formblock
                        key="ident"
                        htmlFor="ident"
                        label={t('airportForm.fields.ident')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="ident"
                          name="ident"
                          className="background-neutral00"
                          placeholder={t('airportForm.fields.placeholders.ident')}
                          value={ident}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetIdent(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                      <Formblock
                        key="name"
                        htmlFor="name"
                        label={t('airportForm.fields.name')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                            // @ts-ignore
                          id="name"
                          name="name"
                          className="background-neutral00"
                          placeholder={t('airportForm.fields.placeholders.name')}
                          value={name || ''}
                          onChange={
                              (
                                /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                              ) => handleSetName(e?.target?.value, formInfo)
                            }
                        />
                      </Formblock>
                      <Formblock
                        key="code"
                        htmlFor="code"
                        label={t('airportForm.fields.code')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                            // @ts-ignore
                          id="code"
                          name="code"
                          className="background-neutral00"
                          placeholder={t('airportForm.fields.placeholders.code')}
                          value={code || ''}
                          onChange={
                              (
                                /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                              ) => handleSetCode(e?.target?.value, formInfo)
                            }
                        />
                      </Formblock>
                      <Formblock
                        key="municipality"
                        htmlFor="municipality"
                        label={t('airportForm.fields.municipality')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="municipality"
                          name="municipality"
                          className="background-neutral00"
                          placeholder={t('airportForm.fields.placeholders.municipality')}
                          value={municipality}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetMunicipality(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                      <Formblock
                        key="coordinate"
                        htmlFor="coordinate"
                        label={t('terminalForm.fields.coordinate')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="coordinate"
                          name="coordinate"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.coordinate')}
                          value={coordinate}
                          onChange={(
                            /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                          ) => handleSetCoordinate(e?.target?.value, formInfo)}
                        />
                      </Formblock>
                      <Formblock
                        key="isoCountry"
                        htmlFor="isoCountry"
                        label={t('airportForm.fields.isoCountry')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="isoCountry"
                          name="isoCountry"
                          className="background-neutral00"
                          placeholder={t('airportForm.fields.placeholders.isoCountry')}
                          value={isoCountry}
                          maxLength={2}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetIsoCountry(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                      <Formblock
                        key="iataCode"
                        htmlFor="iataCode"
                        label={t('airportForm.fields.iataCode')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="iataCode"
                          name="iataCode"
                          className="background-neutral00"
                          placeholder={t('airportForm.fields.placeholders.iataCode')}
                          value={iataCode}
                          maxLength={3}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetIataCode(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                    </div>
                  </FormSection>
                </div>
                <div>
                  <Button
                    className="margin-top-m"
                    form={AIRPORT_FORM}
                    isSubmit
                    onClick={() => {
                      try {
                        const values = formInfo?.formMethods?.getValues ? formInfo.formMethods.getValues() : null;
                        if (values) onSubmit(values);
                      } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error('[DEBUG] error calling manual onSubmit', err);
                      }
                    }}
                    isLoading={
                      postCreateAirportMutation.isLoading || putUpdateAirportMutation.isLoading
                    }
                  >
                    {id ? t('airportForm.actions.update') : t('airportForm.actions.submit')}
                  </Button>
                </div>
              </>
            )}
          </Form>
        ) : null}

        {id ? (
          <div className="margin-top-m">
            <div className="d-flex f-row justify-end align-center">
              <Button icon="add-bold" onClick={handleNewContentClick}>
                {t('airportForm.addTerminalsButton')}
              </Button>
            </div>
            <div className="margin-top-s">
              {isLoadingAirport
                ? <TableSkeleton />
                : generateTableFromSchema(
                  {
                    JSONSchema: UserListSchema,
                    data: airport?.terminals || [],
                    sort: {
                      order,
                      active: sort,
                      handleColumnSort,
                    },
                    navigate: {
                      // @ts-ignore
                      handleRowNavigate,
                    },
                    labelNoResult: t('terminalList.noResult'),
                    customColumns: [
                      {
                        key: 'name',
                        description: '',
                        sortable: false,
                        position: 'end',
                        renderHead: () => t('terminalList.columns.name'),
                        renderCell: (/** @type {Terminal} */row) => (
                          <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                            {row.name}
                          </button>
                        ),
                      },
                      {
                        key: 'identityCode',
                        description: '',
                        sortable: false,
                        position: 'end',
                        renderHead: () => t('terminalList.columns.identityCode'),
                        renderCell: (/** @type {Terminal} */row) => (
                          <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                            {row.identityCode}
                          </button>
                        ),
                      },
                      {
                        key: 'address',
                        description: '',
                        sortable: false,
                        position: 'end',
                        renderHead: () => t('terminalList.columns.address'),
                        renderCell: (/** @type {Terminal} */row) => (
                          <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                            {row.address}
                          </button>
                        ),
                      },
                      {
                        key: 'phone',
                        description: '',
                        sortable: false,
                        position: 'end',
                        renderHead: () => t('terminalList.columns.phone'),
                        renderCell: (/** @type {Terminal} */row) => (
                          <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                            {row.phone}
                          </button>
                        ),
                      },
                    ],
                  },
                )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
